import React, { useContext, useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { Grid, Box } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  colorPicker: {
    width: "100%",
    height: "50px",
    borderRadius: "8px",
    border: "1px solid #e8ecf1",
    cursor: "pointer",
  },
  iconGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(50px, 1fr))",
    gap: "8px",
  },
  iconButton: {
    width: "50px",
    height: "50px",
    borderRadius: "8px",
    border: "2px solid #e8ecf1",
    background: "white",
    cursor: "pointer",
    fontSize: "24px",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      borderColor: "#3b82f6",
      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.2)",
    },
  },
}));

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, "Mínimo 3 caracteres")
    .max(50, "Máximo 50 caracteres")
    .required("Nome é obrigatório"),
  description: Yup.string()
    .max(200, "Máximo 200 caracteres"),
  color: Yup.string(),
  icon: Yup.string(),
});

const ICON_OPTIONS = [
  "📁", "📂", "📋", "📝", "📌", "📍", "🏷️", "🎯",
  "⭐", "💬", "💡", "🔔", "📢", "📣", "✅", "❌",
  "⚡", "🔥", "💎", "🎁", "🎉", "🎊", "🌟", "✨",
];

const COLOR_OPTIONS = [
  "#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#14b8a6", "#f97316", "#6366f1",
];

const QuickMessagesCategoryDialog = ({ open, onClose, categoryId, resetPagination }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState("📁");
  const [selectedColor, setSelectedColor] = useState("#3b82f6");

  useEffect(() => {
    if (categoryId) {
      fetchCategory();
    } else {
      setCategory(null);
      setSelectedIcon("📁");
      setSelectedColor("#3b82f6");
    }
  }, [categoryId, open]);

  const fetchCategory = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/quick-messages-categories/${categoryId}`);
      setCategory(data);
      setSelectedIcon(data.icon || "📁");
      setSelectedColor(data.color || "#3b82f6");
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setLoading(true);
      const data = {
        name: values.name,
        description: values.description,
        icon: selectedIcon,
        color: selectedColor,
      };

      if (categoryId) {
        await api.put(`/quick-messages-categories/${categoryId}`, data);
        toast.success("Categoria atualizada com sucesso!");
      } else {
        await api.post("/quick-messages-categories", data);
        toast.success("Categoria criada com sucesso!");
      }

      setLoading(false);
      resetPagination();
      onClose();
    } catch (err) {
      setLoading(false);
      console.error("Erro ao salvar categoria:", err);
      
      // Mostrar mensagem de erro mais detalhada
      const errorMessage = err.response?.data?.error || err.response?.data?.details || err.response?.data?.message || err.message || "Erro ao salvar categoria";
      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {categoryId ? "Editar Categoria" : "Nova Categoria"}
      </DialogTitle>
      <DialogContent dividers>
        {loading && !category && categoryId ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
            <CircularProgress />
          </div>
        ) : (
          <Formik
            initialValues={{
              name: category?.name || "",
              description: category?.description || "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSave}
            enableReinitialize={true}
          >
            {({ values, errors, touched, isSubmitting, setFieldValue }) => (
              <Form>
                <Grid container spacing={2}>
                  {/* Nome */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Nome da Categoria"
                      variant="outlined"
                      size="small"
                      value={values.name}
                      onChange={(e) => setFieldValue("name", e.target.value)}
                      error={touched.name && !!errors.name}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>

                  {/* Descrição */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="description"
                      label="Descrição (opcional)"
                      variant="outlined"
                      size="small"
                      multiline
                      rows={3}
                      value={values.description}
                      onChange={(e) => setFieldValue("description", e.target.value)}
                      error={touched.description && !!errors.description}
                      helperText={touched.description && errors.description}
                    />
                  </Grid>

                  {/* Cor */}
                  <Grid item xs={12}>
                    <label style={{ fontSize: "12px", fontWeight: 600, color: "#666", display: "block", marginBottom: "8px" }}>
                      Cor
                    </label>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {COLOR_OPTIONS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setSelectedColor(color)}
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "8px",
                            background: color,
                            border: selectedColor === color ? "3px solid #1a1a1a" : "2px solid #e8ecf1",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "scale(1)";
                          }}
                        />
                      ))}
                    </div>
                  </Grid>

                  {/* Ícone */}
                  <Grid item xs={12}>
                    <label style={{ fontSize: "12px", fontWeight: 600, color: "#666", display: "block", marginBottom: "8px" }}>
                      Ícone
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(50px, 1fr))", gap: "8px" }}>
                      {ICON_OPTIONS.map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setSelectedIcon(icon)}
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "8px",
                            background: selectedIcon === icon ? selectedColor : "white",
                            border: selectedIcon === icon ? "none" : "2px solid #e8ecf1",
                            cursor: "pointer",
                            fontSize: "24px",
                            transition: "all 0.2s",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "scale(1)";
                          }}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </Grid>

                  {/* Preview */}
                  <Grid item xs={12}>
                    <Box
                      style={{
                        padding: "16px",
                        borderRadius: "8px",
                        background: "#f9fafb",
                        border: "1px solid #e8ecf1",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "8px",
                          background: selectedColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "20px",
                          color: "white",
                        }}
                      >
                        {selectedIcon}
                      </div>
                      <div>
                        <p style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: 700, color: "#1a1a1a" }}>
                          {values.name || "Nome da categoria"}
                        </p>
                        <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
                          {values.description || "Descrição da categoria"}
                        </p>
                      </div>
                    </Box>
                  </Grid>
                </Grid>

                <DialogActions style={{ marginTop: "24px" }}>
                  <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
                    Cancelar
                  </Button>
                  <ButtonWithSpinner
                    variant="contained"
                    color="primary"
                    type="submit"
                    loading={isSubmitting}
                  >
                    {categoryId ? "Atualizar" : "Criar"}
                  </ButtonWithSpinner>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickMessagesCategoryDialog;
